// src/components/dataSells/SalesTable.jsx
import React, { useState } from 'react';
import {
    FaFilter,
    FaSearch,
    FaDownload,
    FaEye,
    FaChevronLeft,
    FaChevronRight
} from 'react-icons/fa';

/**
 * Componente para mostrar tabla de ventas con filtros
 */
const SalesTable = ({ ventas, marcas, tiendas, onVerDetalle, loading }) => {
    const [filtros, setFiltros] = useState({
        marcaId: '',
        tiendaId: '',
        fechaDesde: '',
        fechaHasta: '',
        sistemaOrigen: '',
        estadoFinanciero: '',
        busqueda: ''
    });

    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina] = useState(20);

    // Filtrar ventas localmente
    const ventasFiltradas = ventas.filter(venta => {
        // Filtro por marca
        if (filtros.marcaId && venta.marca !== filtros.marcaId && venta.marca?._id !== filtros.marcaId) {
            return false;
        }

        // Filtro por tienda
        if (filtros.tiendaId && venta.tienda !== filtros.tiendaId && venta.tienda?._id !== filtros.tiendaId) {
            return false;
        }

        // Filtro por sistema origen
        if (filtros.sistemaOrigen && venta.sistemaOrigen !== filtros.sistemaOrigen) {
            return false;
        }

        // Filtro por estado financiero
        if (filtros.estadoFinanciero && venta.estadoFinanciero !== filtros.estadoFinanciero) {
            return false;
        }

        // Filtro por búsqueda (ID de venta o nombre de cliente)
        if (filtros.busqueda) {
            const busqueda = filtros.busqueda.toLowerCase();
            const idVenta = venta.idVenta?.toLowerCase() || '';
            const nombreCliente = venta.informacionCliente?.nombre?.toLowerCase() || '';
            const emailCliente = venta.informacionCliente?.email?.toLowerCase() || '';

            if (!idVenta.includes(busqueda) && !nombreCliente.includes(busqueda) && !emailCliente.includes(busqueda)) {
                return false;
            }
        }

        // Filtro por fecha
        if (filtros.fechaDesde) {
            const fechaVenta = new Date(venta.fecha);
            const fechaDesde = new Date(filtros.fechaDesde);
            if (fechaVenta < fechaDesde) return false;
        }

        if (filtros.fechaHasta) {
            const fechaVenta = new Date(venta.fecha);
            const fechaHasta = new Date(filtros.fechaHasta);
            fechaHasta.setHours(23, 59, 59, 999);
            if (fechaVenta > fechaHasta) return false;
        }

        return true;
    });

    // Paginación
    const indexUltimoItem = paginaActual * itemsPorPagina;
    const indexPrimerItem = indexUltimoItem - itemsPorPagina;
    const ventasPaginadas = ventasFiltradas.slice(indexPrimerItem, indexUltimoItem);
    const totalPaginas = Math.ceil(ventasFiltradas.length / itemsPorPagina);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const limpiarFiltros = () => {
        setFiltros({
            marcaId: '',
            tiendaId: '',
            fechaDesde: '',
            fechaHasta: '',
            sistemaOrigen: '',
            estadoFinanciero: '',
            busqueda: ''
        });
        setPaginaActual(1);
    };

    const handleExportarCSV = () => {
        // Crear CSV básico
        const headers = ['ID Venta', 'Fecha', 'Monto', 'Moneda', 'Tienda', 'Sistema Origen', 'Estado'];
        const rows = ventasFiltradas.map(venta => [
            venta.idVenta,
            new Date(venta.fecha).toLocaleString('es-ES'),
            venta.monto,
            venta.moneda || 'EUR',
            venta.tienda?.nombre || venta.tienda || 'N/A',
            venta.sistemaOrigen || 'N/A',
            venta.estadoFinanciero || 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `ventas_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-center py-12">
                    <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Header con filtros */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">Ventas Procesadas</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={handleExportarCSV}
                            className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center text-sm"
                            disabled={ventasFiltradas.length === 0}
                        >
                            <FaDownload className="mr-2" />
                            Exportar
                        </button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Búsqueda */}
                    <div className="lg:col-span-2">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por ID venta, cliente o email..."
                                value={filtros.busqueda}
                                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Marca */}
                    <div>
                        <select
                            value={filtros.marcaId}
                            onChange={(e) => setFiltros({ ...filtros, marcaId: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todas las marcas</option>
                            {marcas.map(marca => (
                                <option key={marca._id} value={marca._id}>{marca.nombre}</option>
                            ))}
                        </select>
                    </div>

                    {/* Tienda */}
                    <div>
                        <select
                            value={filtros.tiendaId}
                            onChange={(e) => setFiltros({ ...filtros, tiendaId: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todas las tiendas</option>
                            {tiendas.map(tienda => (
                                <option key={tienda._id} value={tienda._id}>{tienda.nombre}</option>
                            ))}
                        </select>
                    </div>

                    {/* Fecha Desde */}
                    <div>
                        <input
                            type="date"
                            value={filtros.fechaDesde}
                            onChange={(e) => setFiltros({ ...filtros, fechaDesde: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Fecha Hasta */}
                    <div>
                        <input
                            type="date"
                            value={filtros.fechaHasta}
                            onChange={(e) => setFiltros({ ...filtros, fechaHasta: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Sistema Origen */}
                    <div>
                        <select
                            value={filtros.sistemaOrigen}
                            onChange={(e) => setFiltros({ ...filtros, sistemaOrigen: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los sistemas</option>
                            <option value="shopify">Shopify</option>
                            <option value="square">Square</option>
                            <option value="sumup">SumUp</option>
                            <option value="catinfog">Catinfog</option>
                            <option value="woocommerce">WooCommerce</option>
                            <option value="otro">Otro</option>
                        </select>
                    </div>

                    {/* Estado Financiero */}
                    <div>
                        <select
                            value={filtros.estadoFinanciero}
                            onChange={(e) => setFiltros({ ...filtros, estadoFinanciero: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los estados</option>
                            <option value="paid">Pagado</option>
                            <option value="pending">Pendiente</option>
                            <option value="refunded">Reembolsado</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-gray-600">
                        Mostrando {ventasFiltradas.length} de {ventas.length} ventas
                    </p>
                    {(filtros.marcaId || filtros.tiendaId || filtros.fechaDesde || filtros.fechaHasta || filtros.sistemaOrigen || filtros.estadoFinanciero || filtros.busqueda) && (
                        <button
                            onClick={limpiarFiltros}
                            className="text-sm text-blue-500 hover:text-blue-700"
                        >
                            Limpiar filtros
                        </button>
                    )}
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ID Venta
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Fecha
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Monto
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Productos
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Sistema
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {ventasPaginadas.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                    No se encontraron ventas
                                </td>
                            </tr>
                        ) : (
                            ventasPaginadas.map(venta => (
                                <tr key={venta._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {venta.idVenta}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(venta.fecha)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        {formatCurrency(venta.monto, venta.moneda)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {venta.productos?.length || 0} producto(s)
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <span className="capitalize">{venta.sistemaOrigen || 'N/A'}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${venta.estadoFinanciero === 'paid'
                                            ? 'bg-green-100 text-green-800'
                                            : venta.estadoFinanciero === 'pending'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {venta.estadoFinanciero || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button
                                            onClick={() => onVerDetalle(venta)}
                                            className="text-blue-500 hover:text-blue-700 flex items-center"
                                        >
                                            <FaEye className="mr-1" />
                                            Ver
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {paginaActual} de {totalPaginas}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
                            disabled={paginaActual === 1}
                            className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
                            disabled={paginaActual === totalPaginas}
                            className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesTable;

